import Code from '@/components/dashboard/Code';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import Links from '@/docs/ExampleLinks';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function FinancialsAPIDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="Statements - Financials API" description="Query statements data via the Financials API; request parameters, code examples and the response schema for the statements endpoint."  canonicalUrl="https://axionquant.com/docs/03-fundamentals/financials/02-statements" />
      <DocHeading className="text-3xl font-bold text-gray-900 mb-4">Statements - Financials API</DocHeading>
      <div className="space-y-12" ref={sectionRef}>
        {/* ========== 1. Balance Sheet ========== */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /financials/statements/:ticker/balance
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Returns the quarterly balance sheet for the specified company. Results default to the most
                recent quarter. Use the <code className="text-sm">year</code> and <code className="text-sm">quarter</code> query parameters to retrieve a specific period.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Path Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Stock ticker symbol (e.g., &quot;AAPL&quot;, &quot;MSFT&quot;)
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Query Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">year</td>
                      <td className="px-4 py-3 text-sm text-gray-700">integer</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          Optional
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Fiscal year (e.g., <code className="text-xs">2024</code>). Defaults to the latest available year.
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">quarter</td>
                      <td className="px-4 py-3 text-sm text-gray-700">integer</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          Optional
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Fiscal quarter (1&ndash;4). Defaults to the latest available quarter.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <Links links={['/financials/statements/AAPL/balance', '/financials/statements/AAPL/balance?year=2024&quarter=3']} />

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Response</h3>
              <p className="text-sm text-gray-600 mb-2">
                A JSON object containing balance sheet line items in the company&apos;s reporting currency (typically USD). All monetary values are in raw units.
              </p>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">{`{ [key: string]: number | null }`}</p>
                  <p className="text-sm text-gray-600">Returns 60+ balance sheet fields including assets, liabilities, and equity.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Balance Sheet (Apple, latest quarter)" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">Balance Sheet (Apple, latest quarter)</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="financials/statements/balance"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="03-fundamentals/financials/statement-balance"></Code>
              </div>
            </div>
          </div>
        </div>

        {/* ========== 2. Income Statement ========== */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /financials/statements/:ticker/income
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Returns the quarterly income statement for the specified company. Results default to the most
                recent quarter. Use the <code className="text-sm">year</code> and <code className="text-sm">quarter</code> query parameters to retrieve a specific period.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Path Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Stock ticker symbol (e.g., &quot;AAPL&quot;, &quot;MSFT&quot;)
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Query Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">year</td>
                      <td className="px-4 py-3 text-sm text-gray-700">integer</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          Optional
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Fiscal year (e.g., <code className="text-xs">2024</code>). Defaults to the latest available year.
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">quarter</td>
                      <td className="px-4 py-3 text-sm text-gray-700">integer</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          Optional
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Fiscal quarter (1&ndash;4). Defaults to the latest available quarter.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <Links links={['/financials/statements/AAPL/income', '/financials/statements/AAPL/income?year=2024&quarter=3']} />

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Response</h3>
              <p className="text-sm text-gray-600 mb-2">
                A JSON object containing income statement line items in the company&apos;s reporting currency (typically USD).
              </p>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">{`{ [key: string]: number | null }`}</p>
                  <p className="text-sm text-gray-600">Returns revenue, expenses, earnings, and per-share data.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Income Statement (Apple, latest quarter)" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">Income Statement (Apple, latest quarter)</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="financials/statements/income"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="03-fundamentals/financials/statement-income"></Code>
              </div>
            </div>
          </div>
        </div>

        {/* ========== 3. Cash Flow Statement ========== */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /financials/statements/:ticker/cashflow
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Returns the quarterly cash flow statement for the specified company. Results default to the most
                recent quarter. Use the <code className="text-sm">year</code> and <code className="text-sm">quarter</code> query parameters to retrieve a specific period.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Path Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Stock ticker symbol (e.g., &quot;AAPL&quot;, &quot;MSFT&quot;)
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Query Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">year</td>
                      <td className="px-4 py-3 text-sm text-gray-700">integer</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          Optional
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Fiscal year (e.g., <code className="text-xs">2024</code>). Defaults to the latest available year.
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">quarter</td>
                      <td className="px-4 py-3 text-sm text-gray-700">integer</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          Optional
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Fiscal quarter (1&ndash;4). Defaults to the latest available quarter.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <Links links={['/financials/statements/AAPL/cashflow', '/financials/statements/AAPL/cashflow?year=2024&quarter=3']} />

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Response</h3>
              <p className="text-sm text-gray-600 mb-2">
                A JSON object containing cash flow statement line items in the company&apos;s reporting currency (typically USD).
              </p>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">{`{ [key: string]: number | null }`}</p>
                  <p className="text-sm text-gray-600">Returns operating, investing, and financing cash flow data.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Cash Flow (Apple, latest quarter)" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">Cash Flow (Apple, latest quarter)</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="financials/statements/cashflow"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="03-fundamentals/financials/statement-cashflow"></Code>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
